// src/pages/admin/AdminOverview.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart,
  ArrowUp,
  ArrowDown,
  Calendar,
  Activity,
  UserCheck
} from 'lucide-react';
import styles from '../../styles/Overview.module.css';
import useTranslation from '../../hooks/useTranslation';

// Import from adminProviderSlice
import {
  adminGetAllProviders,
} from '../../store/slices/adminProviderSlice';

import {
  selectAdminAllProviders,
  selectAdminLoading,
  selectAdminPagination
} from '../../store/slices/adminProviderSlice';

// Import from adminSlice
import {
  selectAdminStatsSummary,
  selectAdminLoading as selectAdminUserLoading
} from '../../store/slices/adminSlice';

// Import from reportSlice
import {
  getReportStatistics,
  getUnassignedCount,
  getUrgentCount,
  getAllReports
} from '../../store/slices/reportSlice';

import {
  selectReportStatistics,
  selectUnassignedCount,
  selectUrgentCount,
  selectReports,
  selectReportsLoading,
  selectStatisticsLoading,
  selectPendingReports,
  selectUrgentReports,
  selectResolvedReports,
  selectReportError
} from '../../store/slices/reportSlice';

// Import donation selectors
import {
  selectDonationGoals,
  selectPublicSettings,
  getPublicSettings
} from '../../store/slices/platformSettingsSlice';

const AdminOverview = () => {
  const dispatch = useDispatch();
  const [timeRange, setTimeRange] = useState('week');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Redux selectors - Provider
  const providers = useSelector(selectAdminAllProviders);
  const pagination = useSelector(selectAdminPagination);
  const loading = useSelector(selectAdminLoading);
  const adminStatsSummary = useSelector(selectAdminStatsSummary);
  const adminUserLoading = useSelector(selectAdminUserLoading);

  // Report selectors
  const reportStatistics = useSelector(selectReportStatistics);
  const unassignedCount = useSelector(selectUnassignedCount);
  const urgentCount = useSelector(selectUrgentCount);
  const allReports = useSelector(selectReports);
  const reportsLoading = useSelector(selectReportsLoading);
  const statisticsLoading = useSelector(selectStatisticsLoading);
  const pendingReports = useSelector(selectPendingReports);
  const urgentReports = useSelector(selectUrgentReports);
  const resolvedReports = useSelector(selectResolvedReports);
  const reportError = useSelector(selectReportError);

  // Donation selectors
  const donationGoals = useSelector(selectDonationGoals);
  const publicSettings = useSelector(selectPublicSettings);

  // Translation hooks
  const { translatedText: overview } = useTranslation('Overview');
  const { translatedText: totalProviders } = useTranslation('Total Providers');
  const { translatedText: donations } = useTranslation('Donations');
  const { translatedText: reports } = useTranslation('Reports');
  const { translatedText: urgent } = useTranslation('urgent');
  const { translatedText: allClear } = useTranslation('All clear');
  const { translatedText: thisWeek } = useTranslation('This Week');
  const { translatedText: thisMonth } = useTranslation('This Month');
  const { translatedText: thisYear } = useTranslation('This Year');
  const { translatedText: donationTrends } = useTranslation('Donation Trends');
  const { translatedText: recentActivity } = useTranslation('Recent Activity');
  const { translatedText: chartVisualization } = useTranslation('Chart visualization will appear here');
  const { translatedText: noRecentActivity } = useTranslation('No recent activity');
  const { translatedText: quickActions } = useTranslation('Quick Actions');
  const { translatedText: checkReports } = useTranslation('Check Reports');
  const { translatedText: manageDonations } = useTranslation('Manage Donations');
  const { translatedText: loadingDashboard } = useTranslation('Loading dashboard...');
  const { translatedText: donationReceived } = useTranslation('donation received from');
  const { translatedText: urgentReport } = useTranslation('Urgent report submitted by');
  const { translatedText: resolvedReport } = useTranslation('Resolved report');
  const { translatedText: totalMoneyCollected } = useTranslation('Total Money Collected');
  const { translatedText: donationCount } = useTranslation('Total Donations');
  const { translatedText: activeProviders } = useTranslation('Active Providers');

  useEffect(() => {
    let mounted = true;

    const loadProviders = async () => {
      try {
        await dispatch(
          adminGetAllProviders({
            page: 1,
            limit: 10
          })
        ).unwrap();
      } catch (error) {
        console.error('Failed to load providers:', error);
      } finally {
        if (mounted) {
          setIsInitialLoad(false);
        }
      }
    };

    loadProviders();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  // Fetch reports and donations on mount
  useEffect(() => {
    // Fetch report data
    dispatch(getReportStatistics());
    dispatch(getUnassignedCount());
    dispatch(getUrgentCount());
    dispatch(getAllReports({ limit: 10, page: 1 }));
    
    // Fetch donation data
    dispatch(getPublicSettings());
  }, []);

  // Get total providers count
  const getTotalProviders = () => {
    return providers?.length || 0;
  };

  // Get report data from Redux
  const getPendingReportsCount = () => {
    return pendingReports?.length || 0;
  };

  const getUrgentReportsCount = () => {
    return urgentReports?.length || 0;
  };

  const getResolvedReportsCount = () => {
    return resolvedReports?.length || 0;
  };

  // ======================================================
  // DONATION STATISTICS
  // ======================================================
  const getDonationStats = () => {
    const publicDonationIds = publicSettings?.publicDonationIds || [];
    
    let totalCollected = 0;
    let totalDonations = 0;

    if (Array.isArray(publicDonationIds) && publicDonationIds.length > 0) {
      publicDonationIds.forEach(donation => {
        const amount = Number(donation.amount) || 0;
        totalCollected += amount;
        totalDonations++;
      });
    }

    return {
      totalCollected,
      totalDonations
    };
  };

  const donationStats = getDonationStats();

  const getTotalDonations = () => {
    return donationStats.totalCollected || 0;
  };

  const getDonationCount = () => {
    return donationStats.totalDonations || 0;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === 0) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  // Format recent activity from providers, reports and donations
  const getRecentActivity = () => {
    const activities = [];

    // Add provider activities
    if (Array.isArray(providers) && providers.length > 0) {
      const recentProviders = [...providers]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 3);
      
      recentProviders.forEach(provider => {
        activities.push({
          type: 'provider',
          message: `New provider registered: ${provider.businessName || provider.name || 'Unknown'}`,
          time: provider.createdAt || Date.now(),
          providerId: provider.id || provider._id
        });
      });
    }

    // Add donation activities
    const publicDonationIds = publicSettings?.publicDonationIds || [];
    if (Array.isArray(publicDonationIds) && publicDonationIds.length > 0) {
      publicDonationIds.slice(0, 3).forEach(donation => {
        const amount = Number(donation.amount) || 0;
        const donorName = donation.isAnonymous ? 'Anonymous' : (donation.donorName || 'Unknown');
        activities.push({
          type: 'donation',
          message: `${formatCurrency(amount)} ${donationReceived || 'donation received from'} ${donorName}`,
          time: donation.completedAt || donation.createdAt || Date.now(),
          donationId: donation._id
        });
      });
    }

    // Add report activities
    if (allReports && allReports.length > 0) {
      const recentReports = allReports.slice(0, 3);
      recentReports.forEach(report => {
        let message = '';
        
        if (report.severity === 'critical' || report.priority === 'urgent') {
          message = `${urgentReport || 'Urgent report submitted by'} ${report.provider?.businessName || 'Unknown'}`;
        } else if (report.status === 'resolved') {
          message = `${resolvedReport || 'Resolved report'} #${report.reportNumber || report._id?.slice(-6) || 'N/A'}`;
        } else {
          message = `Report #${report.reportNumber || report._id?.slice(-6) || 'N/A'} - ${report.status || 'pending'}`;
        }
        
        activities.push({
          type: 'report',
          message,
          time: report.createdAt || Date.now(),
          reportId: report._id
        });
      });
    }

    // Sort by time (newest first) and limit to 6
    return activities
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 6);
  };

  const statCards = [
    {
      title: totalProviders || 'Total Providers',
      value: getTotalProviders(),
      icon: Users,
      color: '#4F46E5',
      bgColor: '#EEF2FF',
      change: activeProviders || 'Active providers',
      changeType: 'up'
    },
    {
      title: totalMoneyCollected || 'Total Money Collected',
      value: formatCurrency(getTotalDonations()),
      icon: DollarSign,
      color: '#10B981',
      bgColor: '#ECFDF5',
      change: `${getDonationCount()} ${donationCount || 'donations'}`,
      changeType: 'up'
    },
    {
      title: reports || 'Reports',
      value: getPendingReportsCount(),
      icon: AlertCircle,
      color: '#EF4444',
      bgColor: '#FEF2F2',
      change: getUrgentReportsCount() > 0 
        ? `${getUrgentReportsCount()} ${urgent || 'urgent'}` 
        : (allClear || 'All clear'),
      changeType: getUrgentReportsCount() > 0 ? 'down' : 'up'
    }
  ];

  // Loading states
  const isLoading = (loading && isInitialLoad) || adminUserLoading || reportsLoading || statisticsLoading;

  // Show loading only on initial load and no data
  if (isLoading && !providers.length) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{loadingDashboard || 'Loading dashboard...'}</p>
      </div>
    );
  }

  if (reportError) {
    console.error('Report error:', reportError);
  }

  const recentActivities = getRecentActivity();

  return (
    <div className={styles.overviewContainer}>
      {/* Time Range Selector */}
      <div className={styles.timeRangeSelector}>
        <button 
          className={`${styles.timeBtn} ${timeRange === 'week' ? styles.active : ''}`}
          onClick={() => setTimeRange('week')}
        >
          {thisWeek || 'This Week'}
        </button>
        <button 
          className={`${styles.timeBtn} ${timeRange === 'month' ? styles.active : ''}`}
          onClick={() => setTimeRange('month')}
        >
          {thisMonth || 'This Month'}
        </button>
        <button 
          className={`${styles.timeBtn} ${timeRange === 'year' ? styles.active : ''}`}
          onClick={() => setTimeRange('year')}
        >
          {thisYear || 'This Year'}
        </button>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        {statCards.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={styles.statHeader}>
              <div 
                className={styles.statIcon}
                style={{ backgroundColor: stat.bgColor, color: stat.color }}
              >
                <stat.icon size={24} />
              </div>
              <div className={`${styles.statChange} ${stat.changeType === 'up' ? styles.changeUp : styles.changeDown}`}>
                {stat.changeType === 'up' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {stat.change}
              </div>
            </div>
            <div className={styles.statValue}>{stat.value}</div>
            <div className={styles.statTitle}>{stat.title}</div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <h3>{donationTrends || 'Donation Trends'}</h3>
          <div className={styles.chartPlaceholder}>
            <BarChart size={48} />
            <p>{chartVisualization || 'Chart visualization will appear here'}</p>
          </div>
        </div>
        <div className={styles.chartCard}>
          <h3>{recentActivity || 'Recent Activity'}</h3>
          <div className={styles.activityList}>
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} className={styles.activityItem}>
                  <div className={styles.activityIcon}>
                    {activity.type === 'provider' && <Users size={16} />}
                    {activity.type === 'donation' && <DollarSign size={16} />}
                    {activity.type === 'report' && <AlertCircle size={16} />}
                  </div>
                  <div className={styles.activityContent}>
                    <p className={styles.activityText}>{activity.message}</p>
                    <span className={styles.activityTime}>
                      {new Date(activity.time).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.noActivity}>{noRecentActivity || 'No recent activity'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h3>{quickActions || 'Quick Actions'}</h3>
        <div className={styles.actionGrid}>
          <Link to="/admin/reports" className={styles.actionCard}>
            <AlertCircle size={24} />
            <span>{checkReports || 'Check Reports'}</span>
            {getUrgentReportsCount() > 0 && (
              <span className={styles.actionBadgeUrgent}>{getUrgentReportsCount()}</span>
            )}
          </Link>
          <Link to="/admin/donations" className={styles.actionCard}>
            <DollarSign size={24} />
            <span>{manageDonations || 'Manage Donations'}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;